// Copyright (c) 2026 Riddle, LLC. All rights reserved.
// Author: Ray Harris

import { useCallback } from "react";
import { useMsal } from "@azure/msal-react";
import type { IPublicClientApplication } from "@azure/msal-browser";
import { createLoginRequest } from "../authConfig";
import { notifySignInFailed } from "../utils/signInFailureSignal";
import {
  hasAttemptedRestartSignIn,
  markRestartSignInAttempt,
} from "../utils/restartSignInGuard";

export const RESTART_SIGN_IN_EXHAUSTED_MESSAGE =
  "Sign-in could not be completed for this workspace. Please sign in again.";

export async function restartSignIn(instance: IPublicClientApplication): Promise<boolean> {
  try {
    instance.setActiveAccount(null);
  } catch {
  }
  await Promise.resolve()
    .then(() => instance.clearCache())
    .catch(() => {
    });
  if (hasAttemptedRestartSignIn()) {
    notifySignInFailed(RESTART_SIGN_IN_EXHAUSTED_MESSAGE);
    return false;
  }
  markRestartSignInAttempt();
  await instance.loginRedirect({ ...createLoginRequest(), prompt: "select_account" });
  return true;
}

export function useRestartSignIn(): () => Promise<void> {
  const { instance } = useMsal();

  return useCallback(async (): Promise<void> => {
    await restartSignIn(instance);
  }, [instance]);
}
